import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth, can } from "@/context/AuthContext";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Socios from "@/pages/Socios";
import SocioDetalle from "@/pages/SocioDetalle";
import Cuotas from "@/pages/Cuotas";
import Partidos from "@/pages/Partidos";
import Escaner from "@/pages/Escaner";
import Portero from "@/pages/Portero";
import Aforo from "@/pages/Aforo";
import Usuarios from "@/pages/Usuarios";
import Transacciones from "@/pages/Transacciones";
import CajaPartido from "@/pages/CajaPartido";
import ConfigClub from "@/pages/ConfigClub";
import Incidencias from "@/pages/Incidencias";
import MatchDashboard from "@/pages/MatchDashboard";
import Temporadas from "@/pages/Temporadas";
import TiposSocio from "@/pages/TiposSocio";
import BalanceAnual from "@/pages/BalanceAnual";
import AvisoLegal from "@/pages/AvisoLegal";
import Privacidad from "@/pages/Privacidad";

const Protected = ({ children, roles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Cargando...</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !can(user, ...roles)) {
    // Portero default landing: control de accesos. Others: dashboard.
    const fallback = user.role === "portero" ? "/escaner" : "/";
    if (location.pathname === fallback) return <Layout>{children}</Layout>;
    return <Navigate to={fallback} replace />;
  }
  return <Layout>{children}</Layout>;
};

const HomeRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Cargando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "portero") return <Navigate to="/escaner" replace />;
  return <Protected><Dashboard /></Protected>;
};

const RedirectIfAuth = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Cargando...</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster theme="light" position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<RedirectIfAuth><Login /></RedirectIfAuth>} />
            <Route path="/aviso-legal" element={<AvisoLegal />} />
            <Route path="/privacidad" element={<Privacidad />} />
            <Route path="/" element={<HomeRoute />} />
            <Route path="/socios" element={<Protected roles={["admin", "taquilla", "tesoreria", "directiva"]}><Socios /></Protected>} />
            <Route path="/socios/:id" element={<Protected><SocioDetalle /></Protected>} />
            <Route path="/cuotas" element={<Protected roles={["admin", "taquilla", "tesoreria", "directiva"]}><Cuotas /></Protected>} />
            <Route path="/partidos" element={<Protected roles={["admin", "directiva"]}><Partidos /></Protected>} />
            <Route path="/partidos/:id" element={<Protected roles={["admin", "directiva"]}><MatchDashboard /></Protected>} />
            <Route path="/incidencias" element={<Protected><Incidencias /></Protected>} />
            <Route path="/escaner" element={<Protected roles={["admin", "portero"]}><Escaner /></Protected>} />
            <Route path="/portero" element={<Protected roles={["admin", "portero"]}><Portero /></Protected>} />
            <Route path="/aforo" element={<Protected roles={["admin", "portero", "directiva"]}><Aforo /></Protected>} />
            <Route path="/ingresos" element={<Protected roles={["admin", "taquilla", "tesoreria", "directiva"]}><Transacciones tipo="ingreso" /></Protected>} />
            <Route path="/gastos" element={<Protected roles={["admin", "tesoreria", "directiva"]}><Transacciones tipo="gasto" /></Protected>} />
            <Route path="/caja" element={<Protected roles={["admin", "tesoreria", "directiva"]}><CajaPartido /></Protected>} />
            <Route path="/balance" element={<Protected roles={["admin", "tesoreria", "directiva"]}><BalanceAnual /></Protected>} />
            <Route path="/usuarios" element={<Protected roles={["admin"]}><Usuarios /></Protected>} />
            <Route path="/temporadas" element={<Protected roles={["admin"]}><Temporadas /></Protected>} />
            <Route path="/tipos-socio" element={<Protected roles={["admin"]}><TiposSocio /></Protected>} />
            <Route path="/diseno" element={<Protected roles={["admin"]}><ConfigClub /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
